import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../../components/admin/DataTable';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { fetchUsers, fetchOrders, fetchObituaries, fetchInquiries } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';

// 이메일 마스킹: ab****@domain
const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '-';
  const [local, domain] = email.split('@');
  const visible = local.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(local.length - 2, 1))}@${domain}`;
};

const UserManagePage = () => {
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [obituaries, setObituaries] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [detailUser, setDetailUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [u, o, ob, inq] = await Promise.all([
        fetchUsers(),
        fetchOrders(),
        fetchObituaries(),
        fetchInquiries(),
      ]);
      setUsers(u);
      setOrders(o);
      setObituaries(ob);
      setInquiries(inq);
    } catch (e) {
      console.error('회원 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users.filter((u) => {
      const matchSearch =
        !term ||
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.phone?.includes(searchTerm);
      const matchRole = roleFilter === 'all' || u.role === roleFilter;
      return matchSearch && matchRole;
    });
  }, [users, searchTerm, roleFilter]);

  // 본인/마지막 관리자 삭제 가드
  const adminCount = useMemo(() => users.filter((u) => u.role === 'admin').length, [users]);

  const isSelf = (u) =>
    currentUser && (u.id === currentUser.id || u.email === currentUser.email);

  const canDelete = (u) => {
    if (isSelf(u)) return false;
    if (u.role === 'admin' && adminCount <= 1) return false;
    return true;
  };

  const handleRoleToggle = (id) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id ? { ...u, role: u.role === 'admin' ? 'user' : 'admin' } : u
      )
    );
    showToast('회원 권한이 변경되었습니다.', 'success');
    setDetailUser((prev) =>
      prev && prev.id === id ? { ...prev, role: prev.role === 'admin' ? 'user' : 'admin' } : prev
    );
  };

  const confirmDelete = () => {
    setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
    showToast('회원이 삭제되었습니다.', 'success');
    setDeleteTarget(null);
    setDetailUser(null);
  };

  // 회원별 연계 데이터
  const getUserStats = (userId) => {
    const userOrders = orders.filter((o) => o.userId === userId);
    return {
      orders: userOrders,
      orderCount: userOrders.length,
      orderTotal: userOrders.reduce((s, o) => s + (o.totalAmount || 0), 0),
      obituaryCount: obituaries.filter((o) => o.userId === userId).length,
      inquiryCount: inquiries.filter((i) => i.userId === userId).length,
    };
  };

  const roleBadge = (role) => {
    const colors = { admin: 'bg-red-100 text-red-800', user: 'bg-blue-100 text-blue-800' };
    const text = { admin: '관리자', user: '일반회원' };
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {text[role] || role}
      </span>
    );
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true, sortType: 'number' },
    { key: 'name', label: '이름', sortable: true },
    { key: 'email', label: '이메일', sortable: true, render: (email) => maskEmail(email) },
    { key: 'phone', label: '전화번호', sortable: false },
    { key: 'createdAt', label: '가입일', sortable: true, sortType: 'date',
      render: (v) => (v ? formatDate(v, 'yyyy.MM.dd') : '-') },
    { key: 'role', label: '권한', sortable: true, render: (role) => roleBadge(role) },
    {
      key: 'actions', label: '관리', sortable: false,
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setDetailUser(row); }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            상세
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); if (canDelete(row)) setDeleteTarget(row); }}
            disabled={!canDelete(row)}
            title={!canDelete(row) ? '본인 또는 마지막 관리자는 삭제할 수 없습니다.' : undefined}
            className="text-sm font-medium text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            삭제
          </button>
        </div>
      ),
    },
  ];

  const detailStats = detailUser ? getUserStats(detailUser.id) : null;

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">회원 관리</h1>
      </div>

      {error ? (
        <ErrorState onRetry={loadData} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="이름, 이메일, 전화번호로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500 md:w-96"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="권한 필터"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">전체 권한</option>
              <option value="user">일반회원</option>
              <option value="admin">관리자</option>
            </select>
          </div>

          <DataTable
            columns={columns}
            data={filteredUsers}
            onRowClick={(u) => setDetailUser(u)}
            emptyMessage="조건에 맞는 회원이 없습니다."
            loading={loading}
            pageSize={10}
          />

          {!loading && (
            <div className="mt-4 text-sm text-gray-600">
              총 {filteredUsers.length}명의 회원이 있습니다.
            </div>
          )}
        </>
      )}

      {/* 회원 상세 모달 */}
      <Modal isOpen={detailUser !== null} onClose={() => setDetailUser(null)} title="회원 상세" size="lg">
        {detailUser && detailStats && (
          <div className="space-y-5 text-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-lg font-bold text-gray-900">{detailUser.name}</p>
                <p className="text-gray-500">{detailUser.email}</p>
                <p className="text-gray-500">{detailUser.phone}</p>
              </div>
              {roleBadge(detailUser.role)}
            </div>

            <dl className="grid grid-cols-2 gap-3">
              <div className="rounded-md bg-gray-50 p-3">
                <dt className="text-xs text-gray-500">가입일</dt>
                <dd className="font-medium text-gray-900">
                  {detailUser.createdAt ? formatDate(detailUser.createdAt, 'yyyy.MM.dd') : '-'}
                </dd>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <dt className="text-xs text-gray-500">작성 부고</dt>
                <dd className="font-medium text-gray-900">{detailStats.obituaryCount}건</dd>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <dt className="text-xs text-gray-500">주문</dt>
                <dd className="font-medium text-gray-900">
                  {detailStats.orderCount}건 / {formatCurrency(detailStats.orderTotal)}
                </dd>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <dt className="text-xs text-gray-500">1:1 문의</dt>
                <dd className="font-medium text-gray-900">{detailStats.inquiryCount}건</dd>
              </div>
            </dl>

            {detailStats.orders.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold text-gray-700">최근 주문</h4>
                <ul className="divide-y rounded-md border border-gray-200">
                  {detailStats.orders.slice(0, 5).map((o) => (
                    <li key={o.id} className="flex items-center justify-between px-3 py-2">
                      <span className="font-mono text-gray-700">{o.id}</span>
                      <span className="text-gray-900">{formatCurrency(o.totalAmount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-3 border-t border-gray-200 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleRoleToggle(detailUser.id)}
              >
                {detailUser.role === 'admin' ? '일반회원으로 변경' : '관리자로 변경'}
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => canDelete(detailUser) && setDeleteTarget(detailUser)}
                disabled={!canDelete(detailUser)}
              >
                회원 삭제
              </Button>
            </div>
            {!canDelete(detailUser) && (
              <p className="text-xs text-gray-400">
                본인 계정이거나 마지막 관리자는 삭제할 수 없습니다.
              </p>
            )}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="회원 삭제"
        message={
          deleteTarget
            ? `"${deleteTarget.name}" 회원을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.`
            : ''
        }
        confirmLabel="삭제"
        confirmVariant="danger"
      />
    </div>
  );
};

export default UserManagePage;
